var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { EstimateItem } from "./EstimateItem";
import { EstimateType } from "./EstimateType";
/**
 * Creates an empty estimate item with yearly items for 'year', 'year + 1' and 'year + 2'.
 * The first yearly item is required and the estimate is deletable. *
 */
export var createEmptyEstimateItem = function (year) {
    var item = new EstimateItem();
    item.canDelete = true;
    item.type = EstimateType.Unknown;
    item.yearlyItems = [{
            year: year,
            value: null,
            isRequired: true,
            isReadOnly: false
        }, {
            year: year + 1,
            value: null,
            isRequired: false,
            isReadOnly: false
        }, {
            year: year + 2,
            value: null,
            isRequired: false,
            isReadOnly: false
        }];
    return item;
};
/**
 * Estimates
 *  wraps EstimateItem objects and provides discovery and computation methods for its users
 **/
var Estimates = /** @class */ (function () {
    /**
     * Wrap estimate items in an Estimates object. At least one estimate item must be provided and each item must have at least one
     * yearly item.
     */
    function Estimates(items, computeYearlyTotal) {
        var e_1, _a;
        if (computeYearlyTotal === void 0) { computeYearlyTotal = null; }
        if (!items || items.length === 0) {
            throw new Error("Failed to initialize Estimates: at least one estimate item is required.");
        }
        try {
            for (var items_1 = __values(items), items_1_1 = items_1.next(); !items_1_1.done; items_1_1 = items_1.next()) {
                var estimate = items_1_1.value;
                if (!estimate.yearlyItems || estimate.yearlyItems.length === 0) {
                    throw new Error("Failed to initialize Estimates: at least one yearly items is required for each estimate.");
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (items_1_1 && !items_1_1.done && (_a = items_1.return)) _a.call(items_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        this._computeYearlyTotal = computeYearlyTotal;
        this.items = items;
    }
    Object.defineProperty(Estimates.prototype, "years", {
        /**
         * Gets the available years
         */
        get: function () {
            var e_2, _a, e_3, _b;
            var yearSet = new Set();
            try {
                for (var _c = __values(this.items), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var estimate = _d.value;
                    try {
                        for (var _e = (e_3 = void 0, __values(estimate.yearlyItems)), _f = _e.next(); !_f.done; _f = _e.next()) {
                            var yearItem = _f.value;
                            yearSet.add(yearItem.year);
                        }
                    }
                    catch (e_3_1) { e_3 = { error: e_3_1 }; }
                    finally {
                        try {
                            if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                        }
                        finally { if (e_3) throw e_3.error; }
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_2) throw e_2.error; }
            }
            var arr = Array.from(yearSet.values()).sort();
            return arr;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Estimates.prototype, "grandTotal", {
        /**
         * Computes the grand total (sum of all cells)
         */
        get: function () {
            var totals = this.yearlyTotals.map(function (yearlyTotal) { return yearlyTotal.total; });
            return totals.reduce(function (prevTotal, nextTotal) { return prevTotal + nextTotal; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Estimates.prototype, "yearlyTotals", {
        /**
         * Gets the yearly totals
         */
        get: function () {
            var e_4, _a;
            var totals = [];
            try {
                for (var _b = __values(this.years), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var year = _c.value;
                    totals.push({
                        year: year,
                        total: this.computeYearlyTotal(year)
                    });
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_4) throw e_4.error; }
            }
            return totals;
        },
        enumerable: false,
        configurable: true
    });
    /**
    * Gets the total for the given year, or 0 if called for non-existent year
    */
    Estimates.prototype.yearlyTotal = function (year) {
        var yearTotal = this.yearlyTotals
            .find(function (total) { return total.year == year; });
        if (yearTotal)
            return yearTotal.total;
        else
            throw new Error("No total found for " + year + ".");
    };
    Object.defineProperty(Estimates.prototype, "itemTotals", {
        /**
         * Gets the item totals
         */
        get: function () {
            var e_5, _a;
            var totals = [];
            try {
                for (var _b = __values(this.items), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var estimate = _c.value;
                    totals.push({
                        name: estimate.name,
                        total: this.computeItemTotal(estimate.name)
                    });
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_5) throw e_5.error; }
            }
            return totals;
        },
        enumerable: false,
        configurable: true
    });
    /**
    * Gets the total for the given item, or 0 if called for non-existent item
    */
    //private itemTotal(name: string): number {
    //  let itemTotal = this.itemTotals
    //    .find(total => total.name == name)
    //  if (itemTotal)
    //    return itemTotal.total
    //  else
    //    throw new Error(`No total found for ${name}.`);
    //}
    /**
     * Computes yearly total
     * TODO: Refactor to use Vue props in a smart way
     * Using functions causes totals to be counted #cols times
     * when we only need to do 1 computation
     *
     * Using _computeYearlyTotal if it has been given a real function in constructor.
     * Default computation otherwise.
     */
    Estimates.prototype.computeYearlyTotal = function (year) {
        if (typeof (this._computeYearlyTotal) == "function") {
            return this._computeYearlyTotal(year);
        }
        return this.items
            .map(function (estimate) {
            if (estimate.ignoreForCalculation)
                return 0;
            var value = estimate.yearlyItems
                .find(function (item) { return item.year == year; })
                .value;
            return Number.isInteger(value) ? value : 0;
        })
            .reduce(function (prev, next) { return prev + next; });
    };
    /**
     * Computes item total
     * TODO: Refactor to use a smart cache as
     * Using functions causes totals to be counted #rows times
     * when we only need to do 1 computation
     */
    Estimates.prototype.computeItemTotal = function (name) {
        var item = this.items
            .find(function (item) { return item.name == name; });
        if (item && !item.ignoreForCalculation)
            return this.computeEstimateTotal(item);
        else
            return 0;
    };
    Estimates.prototype.computeEstimateTotal = function (estimate) {
        if (estimate.ignoreForCalculation)
            return 0;
        return estimate.yearlyItems
            .map(function (item) { return Number.isInteger(item.value) ? item.value : 0; })
            .reduce(function (prev, next) { return prev + next; });
    };
    return Estimates;
}());
export default Estimates;
